import React from 'react';
import { ChevronRight } from 'lucide-react';

/**
 * Where the signed-in person sits in the team, and how each person directly
 * below them is doing. A team lead sees "Admin › Manager › you" and a row per
 * recruiter; a manager a row per team lead; the admin a row per manager. Each
 * row totals the whole branch under that person, from the jobs and candidates
 * this dashboard already loaded — so it can never show anything the viewer
 * could not open themselves.
 */

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  manager: 'Manager',
  team_lead: 'Team Lead',
  recruiter: 'Recruiter',
};

const ROLE_COLORS = {
  super_admin: '#f87171',
  admin: '#a78bfa',
  manager: '#38bdf8',
  team_lead: '#34d399',
  recruiter: '#94a3b8',
};

const REJECTED = ['rejected_resume', 'rejected_interview', 'rejected_final'];

function RoleChip({ role }) {
  const color = ROLE_COLORS[role] || '#94a3b8';
  return (
    <span style={{
      fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
      color, background: `${color}1f`, border: `1px solid ${color}55`, whiteSpace: 'nowrap',
    }}>
      {ROLE_LABELS[role] || role}
    </span>
  );
}

function branchStats(branchIds, jobs, candidates) {
  const ids = new Set(branchIds);
  const branchJobs = jobs.filter(j => j.status !== 'Deleted' && ids.has(j.assigned_to_id));
  const jobIds = new Set(branchJobs.map(j => j.id));
  const cands = candidates.filter(c => jobIds.has(c.job_id) && !c.is_deleted);
  return {
    jobs: branchJobs.length,
    activeJobs: branchJobs.filter(j => j.status === 'Active').length,
    candidates: cands.length,
    interviewed: cands.filter(c => c.interview_status === 'Completed').length,
    selected: cands.filter(c => c.pipeline_stage === 'completed').length,
    rejected: cands.filter(c => REJECTED.includes(c.pipeline_stage)).length,
  };
}

export default function TeamOverview({ team, jobs, candidates }) {
  if (!team) return null;
  const { chain = [], reports = [], me } = team;
  const showChain = chain.length > 1;
  if (!showChain && reports.length === 0) return null;

  const rows = reports.map(r => ({ ...r, stats: branchStats(r.branch_ids, jobs, candidates) }));
  const reportLabel = me?.role === 'team_lead' ? 'recruiter' : 'person';

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      {showChain && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: rows.length ? 14 : 0 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-faint)', marginRight: 4 }}>
            Reporting line
          </span>
          {chain.map((p, i) => (
            <React.Fragment key={p.id || `top-${i}`}>
              {i > 0 && <ChevronRight size={14} style={{ color: 'var(--text-faint)' }} />}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} title={p.email || undefined}>
                <strong style={{ fontSize: 13.5, color: 'var(--text-heading)' }}>
                  {p.name}{p.id === me?.id ? ' (you)' : ''}
                </strong>
                <RoleChip role={p.role} />
              </span>
            </React.Fragment>
          ))}
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="card-header" style={{ marginBottom: 8 }}>
            <div className="card-title">Your team</div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {rows.length} {rows.length === 1 ? reportLabel : `${reportLabel === 'person' ? 'people' : 'recruiters'}`} report to you · totals include everyone under them
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Name</th>
                  {me?.role !== 'team_lead' && <th>Team size</th>}
                  <th>Jobs</th>
                  <th>Active</th>
                  <th>Candidates</th>
                  <th>Interviewed</th>
                  <th>Selected</th>
                  <th>Rejected</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} style={{ opacity: r.is_active ? 1 : 0.55 }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <strong style={{ color: 'var(--text-heading)' }}>{r.name}</strong>
                        <RoleChip role={r.role} />
                        {!r.is_active && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Inactive</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.email}</div>
                    </td>
                    {me?.role !== 'team_lead' && <td>{Math.max(r.branch_ids.length - 1, 0)}</td>}
                    <td style={{ fontWeight: 600 }}>{r.stats.jobs}</td>
                    <td>{r.stats.activeJobs}</td>
                    <td style={{ fontWeight: 600 }}>{r.stats.candidates}</td>
                    <td>{r.stats.interviewed}</td>
                    <td style={{ color: r.stats.selected ? 'var(--green)' : undefined, fontWeight: r.stats.selected ? 600 : 400 }}>{r.stats.selected}</td>
                    <td style={{ color: r.stats.rejected ? 'var(--red)' : undefined }}>{r.stats.rejected}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
